import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IModalState } from '../../../types/stateTypes';
import LoginService from '../../../services/login/loginApi-service';
import StandardResponse from '../../../models/standard-response';
import { ITermsConditionData } from '../../../models/login';
import { AppState } from '../../../store/state/app.state';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { IAcceptTermsConditionInput } from '../../../models/terms-conditions';
import { logout, setCurrencyDetails, userDetail } from '../../../store/actions/app.Actions';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';

export const useAcceptTermsAndCondition = () => {
    const maxHeight = 350;
    const userData = useSelector((state: AppState) => state.UserData);
    const loginService = new LoginService();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [termsConditionList, setTermsConditionList] = useState<Array<ITermsConditionData>>([]);
    const [requiredTermsAndCondition, setRequiredTermsAndCondition] = useState<Array<number>>([]);
    const [selectedTermsAndCondition, setSelectedTermsAndCondition] = useState<Array<number>>([]);
    const [isReachedToBottom, setIsReachedToBottom] = useState<boolean>(false);
    const [modal, setModal] = useState<IModalState>({
        body: null,
        header: '',
        showModal: false,
        id: null,
    });
    const { t } = useTranslation();

    useEffect(() => {
        getTermsConditionListing();
    }, []);

    useEffect(() => {
        if (modal.showModal) {
            const termsConditionElement = document.getElementById('terms_condition');
            if (termsConditionElement && termsConditionElement.scrollHeight <= maxHeight) {
                setIsReachedToBottom(true);
            }
        }
    }, [modal.showModal]);

    const trackScrolling = () => {
        const wrappedElement = document.getElementById('terms_condition');
        if (wrappedElement !== null && isBottom(wrappedElement)) {
            setIsReachedToBottom(true);
        }
    };

    const isBottom = (el: HTMLElement) => {
        const height = document.getElementsByClassName('modal-content')[0].clientHeight;
        return el.scrollTop + height >= el.scrollHeight;
    };

    const getTermsConditionListing = async () => {
        let requiredTermsAndConditionIds: Array<number> = [];
        let TermsConditionList: Array<ITermsConditionData> = [];
        await loginService
            .getTermsAndConditionListing()
            .then(async (res: StandardResponse<Array<ITermsConditionData>>) => {
                if (userData && userData.has_saved_terms) {
                    if (Array.isArray(res.Data)) {
                        TermsConditionList = res.Data.filter(
                            (x) => x.accepted === null || (x.mandatory === true && x.accepted === false),
                        );
                    }
                } else {
                    TermsConditionList = res.Data;
                }
                if (Array.isArray(TermsConditionList)) {
                    const requiredTermsCondition = TermsConditionList.filter((x) => x.mandatory === true);
                    if (Array.isArray(requiredTermsCondition)) {
                        requiredTermsCondition.forEach((data) => {
                            requiredTermsAndConditionIds.push(data.id);
                        });
                    }
                }
                setTermsConditionList(TermsConditionList);
                setRequiredTermsAndCondition(requiredTermsAndConditionIds);
            })
            .catch((m) => {
                toast.error(m.toString());
            });
    };

    const handleSubmit = async () => {
        let acceptedAllTermsCondition = true;
        requiredTermsAndCondition.forEach((data) => {
            if (!selectedTermsAndCondition.includes(data)) {
                acceptedAllTermsCondition = false;
            }
        });

        if (acceptedAllTermsCondition) {
            const terms_conditions: Array<IAcceptTermsConditionInput> = [];
            termsConditionList.forEach((data) => {
                terms_conditions.push({
                    id: data.id,
                    accepted: selectedTermsAndCondition.includes(data.id),
                });
            });
            const input = { terms_conditions };

            await loginService
                .saveTermsAndCondition(input)
                .then(async (_res: StandardResponse<number>) => {
                    await loginService
                        .userDetails()
                        .then((userDetails) => {
                            dispatch(userDetail(userDetails));
                            dispatch(setCurrencyDetails(userDetails?.Data?.currency_details));
                            navigate('/');
                        })
                        .catch((m) => {
                            if (
                                m.response &&
                                m.response.data &&
                                m.response.data.message &&
                                m.response.data.message.includes('Organisation is deleted')
                            ) {
                                toast.error(m.response.data.message);
                            } else if (m && m.includes('Organisation is deleted')) {
                                toast.error(m.toString());
                            } else {
                                toast.error(t('login.toast.error'));
                            }
                            dispatch(logout());
                        });
                })
                .catch((m) => {
                    if (m.response && m.response.message) {
                        toast.error(m.response.message);
                    } else {
                        toast.error(m.toString());
                    }
                });
        } else {
            toast.error(t('termsAndConditionListing.errors.acceptTermCondition'));
        }
    };

    const acceptTermsCondition = (id: number) => {
        let selectedTermsAndConditions = [...selectedTermsAndCondition];
        selectedTermsAndConditions.push(id);
        setSelectedTermsAndCondition(selectedTermsAndConditions);
        setModal((prevModal) => ({
            ...prevModal,
            body: '',
            header: '',
            id: null,
            showModal: false,
        }));
        setIsReachedToBottom(false);
    };

    const handleModal = (body: string, header: string, showModal: boolean | null, id: number | null) => {
        setModal((prevModal) => ({
            ...prevModal,
            body,
            header,
            showModal,
            id,
        }));
        setIsReachedToBottom(false);
    };

    const handleCheckChange = (data: ITermsConditionData) => {
        if (selectedTermsAndCondition.includes(data.id)) {
            const array = [...selectedTermsAndCondition];
            const index = array.indexOf(data.id);
            if (index !== -1) {
                array.splice(index, 1);
                setSelectedTermsAndCondition(array);
            }
        } else {
            handleModal(data.content, data.name, true, data.id);
        }
    };

    return {
        userData,
        termsConditionList,
        requiredTermsAndCondition,
        selectedTermsAndCondition,
        isReachedToBottom,
        modal,
        trackScrolling,
        getTermsConditionListing,
        handleSubmit,
        acceptTermsCondition,
        handleModal,
        handleCheckChange,
    };
};
